import { useEffect, useRef, useState } from 'react';
import { Camera, Loader2, RefreshCw, Save, Upload } from 'lucide-react';
import { toast } from 'sonner';
import {
  captureAllAutomationStepPreviews,
  linkMapNodeToAutomation,
  listAutomationsForMap,
  materializeAutomationStepChain,
  syncMapNodeFromAutomation,
  uploadAutomationStepScreenshot,
  type AutomationRowForMap,
} from '../../lib/systemMapAutomation';
import type { MapNodeRow } from '../../lib/systemMap';

interface SystemMapAutomationPanelProps {
  node: MapNodeRow;
  saving: boolean;
  setSaving: (value: boolean) => void;
  onNodeUpdated: (node: MapNodeRow) => void;
  onMapReload?: () => void;
}

export default function SystemMapAutomationPanel({
  node,
  saving,
  setSaving,
  onNodeUpdated,
  onMapReload,
}: SystemMapAutomationPanelProps) {
  const [automations, setAutomations] = useState<AutomationRowForMap[]>([]);
  const [loadingAutomations, setLoadingAutomations] = useState(false);
  const [selectedAutomationId, setSelectedAutomationId] = useState(
    node.annotations.automation?.automationId ?? '',
  );
  const stepInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const workflow = node.annotations.automation;

  useEffect(() => {
    setSelectedAutomationId(workflow?.automationId ?? '');
  }, [node.id, workflow?.automationId]);

  useEffect(() => {
    let cancelled = false;
    setLoadingAutomations(true);
    void listAutomationsForMap()
      .then((rows) => {
        if (!cancelled) setAutomations(rows);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          toast.error(e instanceof Error ? e.message : 'Failed to load automations');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingAutomations(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLinkAutomation = async () => {
    if (!selectedAutomationId) {
      toast.error('Choose an automation');
      return;
    }
    setSaving(true);
    try {
      const updated = await linkMapNodeToAutomation(node, selectedAutomationId);
      onNodeUpdated(updated);
      toast.success('Automation linked');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to link automation');
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    setSaving(true);
    try {
      const updated = await syncMapNodeFromAutomation(node);
      onNodeUpdated(updated);
      onMapReload?.();
      toast.success('Synced from Engine');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to sync automation');
    } finally {
      setSaving(false);
    }
  };

  const handleBuildChain = async () => {
    setSaving(true);
    try {
      const { parent } = await materializeAutomationStepChain(node, node.map_id);
      onNodeUpdated(parent);
      onMapReload?.();
      toast.success('Step chain built to the right of parent');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to build step chain');
    } finally {
      setSaving(false);
    }
  };

  const handleCaptureAll = async () => {
    setSaving(true);
    try {
      const updated = await captureAllAutomationStepPreviews(node);
      onNodeUpdated(updated);
      if (node.annotations.automation?.layout === 'chain' || workflow?.layout === 'chain') {
        await materializeAutomationStepChain(updated, node.map_id);
        onMapReload?.();
      }
      toast.success('Captured step previews');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to capture previews');
    } finally {
      setSaving(false);
    }
  };

  const handleStepUpload = async (stepId: string, file: File) => {
    setSaving(true);
    try {
      const updated = await uploadAutomationStepScreenshot(node, stepId, file);
      onNodeUpdated(updated);
      toast.success('Step screenshot uploaded');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to upload screenshot');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-4">
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
          Automation workflow
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Link an Engine automation, sync step timing, then capture or upload screenshots for each
          email in the sequence.
        </p>
        <div className="flex flex-col gap-2">
          <select
            value={selectedAutomationId}
            onChange={(e) => setSelectedAutomationId(e.target.value)}
            disabled={saving || loadingAutomations}
            className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
          >
            <option value="">Select automation…</option>
            {automations.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
                {row.alias ? ` (${row.alias})` : ''}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => void handleLinkAutomation()}
            disabled={saving || !selectedAutomationId}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            <Save className="w-3.5 h-3.5" />
            Link automation
          </button>
        </div>
      </div>

      {workflow ? (
        <>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void handleSync()}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
              Sync steps
            </button>
            <button
              type="button"
              onClick={() => void handleBuildChain()}
              disabled={saving || workflow.steps.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-indigo-300 dark:border-indigo-700 text-indigo-800 dark:text-indigo-200 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 disabled:opacity-50"
            >
              Build step chain
            </button>
            <button
              type="button"
              onClick={() => void handleCaptureAll()}
              disabled={saving || workflow.steps.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              <Camera className="w-3.5 h-3.5" />
              Capture all previews
            </button>
          </div>

          {workflow.layout === 'chain' ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Chain layout: each step is a separate node to the right. Edge labels show day offset and
              send time. Rebuild after sync to add/remove steps; drag step nodes to adjust layout.
            </p>
          ) : (
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
              Steps ({workflow.steps.length})
            </h3>
            <div className="space-y-3">
              {workflow.steps.map((step) => (
                <div
                  key={step.stepId}
                  className="rounded-lg border border-gray-200 dark:border-gray-700 p-2 space-y-2"
                >
                  <div>
                    <div className="text-xs font-medium text-gray-900 dark:text-white">
                      {step.scheduleLabel}
                    </div>
                    {step.name ? (
                      <div className="text-[11px] text-gray-500 dark:text-gray-400">{step.name}</div>
                    ) : null}
                  </div>
                  {step.screenshotUrl ? (
                    <img
                      src={step.screenshotUrl}
                      alt=""
                      className="w-full rounded border border-gray-200 dark:border-gray-700"
                    />
                  ) : (
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">No screenshot yet.</p>
                  )}
                  <input
                    ref={(el) => {
                      stepInputRefs.current[step.stepId] = el;
                    }}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void handleStepUpload(step.stepId, file);
                      e.target.value = '';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => stepInputRefs.current[step.stepId]?.click()}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    {step.screenshotUrl ? 'Replace screenshot' : 'Upload screenshot'}
                  </button>
                </div>
              ))}
            </div>
          </div>
          )}
        </>
      ) : null}
    </div>
  );
}
